// App.tsx
/**
 * Main Application Component
 *
 * This file has been refactored to use a declarative route configuration system.
 * Route definitions have been moved to:
 *   - src/routes/config/authRoutes.ts
 *   - src/routes/config/publicRoutes.ts
 *   - src/routes/config/playerRoutes.ts
 *   - src/routes/config/tournamentRoutes.ts
 *   - src/routes/config/staffRoutes.ts
 *   - src/routes/config/adminRoutes.ts
 *
 * Route wrapper components have been extracted to:
 *   - src/routes/components/
 */

import { ReactElement } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Theme
import { theme } from './theme';

// Context Providers
import { AuthProvider } from './context/AuthContext';
import { TournamentProvider } from './context/TournamentContext';
import { NotificationProvider } from './context/NotificationContext';

// Routes
import { RouteRenderer, allRoutes } from './routes';

// Post-login gates
import { StoreProfileGate } from './components/auth/StoreProfileGate';

// Maintenance mode — admin-toggled fullscreen block for non-admins.
import { MaintenanceGate } from './components/MaintenanceGate';

// Web Push (FCM) — foreground delivery + soft opt-in prompt.
import { PushNotificationManager } from './features/notifications';
// Diagnostics: admin-toggled permission-deny capture (off by default).
import { DenyLoggerMount } from './features/diagnostics/DenyLoggerMount';

// Motion config — sets prefers-reduced-motion respect + default
// transition for every framer-motion component in the tree.
// No-op for classic-theme users; the minimal-theme components are
// the consumers.
import { RootMotionProvider } from './components/minimal';

// Judge Assist — floating chat bubble for staff, backed by the
// server/judge-assist.mjs API. Renders globally on top of any page.
import { JudgeAssistWidget } from './features/judge-assist';

// Guided tour — role-aware in-app walkthrough (staff / player / store).
// Provider owns state + first-run invite; GuidedTour renders the overlay.
import { TourProvider, GuidedTour } from './features/tour';

// ============================================
// MAIN APP
// ============================================

function App(): ReactElement {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <NotificationProvider>
          <AuthProvider>
            <TournamentProvider>
              <RootMotionProvider>
               <TourProvider>
                <RouteRenderer routes={allRoutes} />
                {/* Sibling of the route tree so it can render on
                    top of any page a store owner lands on
                    post-login. Self-hides when the user record
                    has both names set. */}
                <StoreProfileGate />
                {/* Sibling of the route tree: fullscreen maintenance screen
                    for everyone but admins while config/maintenance/enabled
                    is on (toggled from the Admin Dashboard). Renders nothing
                    otherwise. */}
                <MaintenanceGate />
                {/* Sibling of the route tree: surfaces foreground push
                    messages as toasts and shows the soft opt-in prompt.
                    Self-hides when the user has decided or push is
                    unsupported. */}
                <PushNotificationManager />
                {/* Sibling of the route tree: runs the permission-deny
                    diagnostic on every client when an admin enables it. */}
                <DenyLoggerMount />
                {/* Sibling of the route tree: floating Judge Assist chat
                    bubble. Self-hides when no user is logged in. */}
                <JudgeAssistWidget />
                {/* Sibling of the route tree: role-aware guided-tour overlay
                    and first-run invite. Self-hides when no tour is running. */}
                <GuidedTour />
               </TourProvider>
              </RootMotionProvider>
            </TournamentProvider>
          </AuthProvider>
        </NotificationProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
